/**
 * KODY, KTÓRE TRAFIAJĄ DO BAZY SPS-a — jedna reguła dla grup i artykułów.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „walidację już na samym polu, by nie pozwalało wpisać więcej niż
 *  32 znaki… dodatkowo trimuj spacje z przodu i tyłu przy zapisie (pomiędzy wyrazami mogą być)…
 *  nie może być też w kodzie grupy polskich znaków (nie pozwalaj ich wpisać z klawiatury)".
 *  To samo dotyczy kodu katalogowego artykułu.
 *
 *  ⚠️ JEDEN MODUŁ, BO TO JEDNA REGUŁA. Dwa pola w dwóch oknach pilnujące tego samego osobno
 *  rozjechałyby się przy pierwszej poprawce — a rozjazd byłby widoczny dopiero w bazie klienta,
 *  na tym z dwóch ekranów, o którym ktoś zapomniał.
 *
 *  ⚠️ ODMOWA PRZY PISANIU, NIE PRZY ZAPISIE. Pole, które przyjmuje 40 znaków i odrzuca je dopiero
 *  po kliknięciu „Zapisz", każe człowiekowi zgadywać, co usunąć. Tu znak spoza reguły po prostu
 *  nie wchodzi, a dymek mówi dlaczego.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Tyle znaków mieści kolumna SPS-a (`USERGROUPNUMBER`, `ITEMNUMBER`). */
export const MAX_KOD = 32;

/**
 * Znaki dopuszczone w kodzie.
 *
 * ⚠️⚠️ SPACJA WEWNĄTRZ TAK, NA BRZEGACH NIE — dokładnie jak w zamówieniu. Spacja w środku bywa
 * w kodach u klienta („REK NITRYL"), ale wiodąca albo końcowa jest niewidoczna i robi z „BIURO "
 * inny klucz niż „BIURO". Dlatego przycinamy dopiero przy zapisie, a nie odbieramy prawa do
 * wpisania spacji.
 *
 * ⚠️ POLSKIE LITERY WYKLUCZONE ŚWIADOMIE: kod jest kluczem naturalnym w SPS i wraca do nas
 * z bazy klienta, gdzie o kodowaniu decyduje strona kodowa instalacji. Nazwa grupy takiego
 * ograniczenia NIE MA i mieć nie powinna — to opis dla człowieka.
 */
const ZLE_ZNAKI = /[^A-Za-z0-9 _.\-/+]/g;

/**
 * To samo bez flagi `g`.
 *
 * ⚠️⚠️ DWA WYRAŻENIA, BO `.test()` NA WYRAŻENIU Z `g` JEST STANOWY. Zapamiętuje `lastIndex`
 * między wywołaniami, więc ten sam znak sprawdzony dwa razy pod rząd daje `true`, a potem
 * `false` — usterka, która w przeglądarce objawia się „co drugi znak przechodzi". Złapał to
 * test przechodzący pętlą po wszystkich polskich literach; przy jednym sprawdzeniu na przypadek
 * nie byłoby jej widać.
 */
const ZLY_ZNAK = new RegExp(ZLE_ZNAKI.source);

/** Czy ten znak wolno wpisać w kod. */
export const znakDozwolonyWKodzie = (znak: string): boolean => !ZLY_ZNAK.test(znak);

/**
 * Oczyszczenie tego, co człowiek wpisał ALBO WKLEIŁ.
 *
 * ⚠️⚠️ WKLEJANIE JEST TU WAŻNIEJSZE OD PISANIA. Blokada pojedynczych klawiszy nie dotyczy
 * `Ctrl+V`, a kody najczęściej wklei się z arkusza — razem z polskimi literami, tabulatorem
 * i spacją na końcu. Dlatego czyścimy WARTOŚĆ pola, nie zdarzenia klawiatury.
 */
export function oczysc(surowy: string): string {
  return surowy.replace(ZLE_ZNAKI, '').slice(0, MAX_KOD);
}

/**
 * Postać do zapisu.
 *
 * ⚠️ TRIM DOPIERO TUTAJ, nie w trakcie pisania: przycinanie na bieżąco nie pozwoliłoby wpisać
 * spacji w środku, bo każda kończyłaby się na brzegu wartości w chwili jej wpisania.
 */
export const doZapisu = (surowy: string): string => oczysc(surowy).trim();

/** Czy w tym, co człowiek wpisał, było coś, czego nie przyjmujemy. */
export function powodOdrzucenia(surowy: string): 'DLUGOSC' | 'ZNAKI' | null {
  const bezZlych = surowy.replace(ZLE_ZNAKI, '');
  if (bezZlych !== surowy) return 'ZNAKI';
  if (surowy.length > MAX_KOD) return 'DLUGOSC';
  return null;
}
